<template>
  <div>
    <!-- Drag & Drop Zone -->
    <div
      class="upload-zone"
      :class="{ 'upload-zone--active': isDragging, 'upload-zone--error': hasError }"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="'.pdf,.png,.jpg,.jpeg,.csv,.xlsx,.ods'"
        style="display: none"
        @change="onFileInputChange"
      />
      <div class="upload-zone__content">
        <v-icon :color="isDragging ? 'primary' : 'grey lighten-1'" size="32">mdi-cloud-upload-outline</v-icon>
        <div class="caption grey--text mt-1">Povucite datoteke ovdje ili kliknite za odabir</div>
        <div class="caption grey--text lighten-1">PDF, PNG, JPG, CSV, XLSX, ODS &bull; max 10 datoteka &bull; max 5MB ukupno</div>
      </div>
    </div>

    <!-- Validation errors -->
    <div v-if="validationError" class="error--text caption mt-1">
      <v-icon small color="error">mdi-alert-circle</v-icon>
      {{ validationError }}
    </div>

    <!-- File list -->
    <div v-if="value && value.length > 0" class="mt-2">
      <div
        v-for="(att, index) in value"
        :key="index"
        class="attachment-item d-flex align-center py-1"
      >
        <v-icon small :color="fileIconColor(att.mimeType)" class="mr-2">{{ fileIcon(att.mimeType) }}</v-icon>
        <div class="flex-grow-1 mr-2" style="min-width: 0">
          <div class="caption text-truncate">{{ att.name }}</div>
          <div v-if="att.status === 'uploading'" class="mt-1">
            <v-progress-linear
              :value="att.progress"
              height="4"
              color="primary"
              rounded
            ></v-progress-linear>
          </div>
          <div v-else class="caption grey--text">
            <span v-if="att.status === 'done'" class="success--text">
              <v-icon x-small color="success">mdi-check-circle</v-icon>
              Učitano &bull; {{ formatSize(att.size) }}
            </span>
            <span v-else-if="att.status === 'error'" class="error--text">
              <v-icon x-small color="error">mdi-alert-circle</v-icon>
              {{ att.error || 'Greška pri uploadanju' }}
            </span>
            <span v-else>{{ formatSize(att.size) }}</span>
          </div>
        </div>
        <v-btn icon x-small @click.stop="removeAttachment(index)" :disabled="att.status === 'uploading'">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { storage } from '@/plugins/firebase'

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet'
]

const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.csv', '.xlsx', '.ods']

const MAX_FILES = 10
const MAX_TOTAL_SIZE = 5 * 1024 * 1024 // 5MB in bytes

export default {
  name: 'EracunAttachmentUploader',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    companyId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isDragging: false,
      validationError: null,
      dragCounter: 0
    }
  },
  computed: {
    hasError () {
      return !!this.validationError
    }
  },
  methods: {
    triggerFileInput () {
      this.$refs.fileInput.click()
    },

    onDragLeave () {
      this.dragCounter--
      if (this.dragCounter <= 0) {
        this.dragCounter = 0
        this.isDragging = false
      }
    },

    onDrop (event) {
      this.isDragging = false
      this.dragCounter = 0
      const files = Array.from(event.dataTransfer.files)
      this.processFiles(files)
    },

    onFileInputChange (event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
      // Reset input so the same file can be re-selected
      event.target.value = ''
    },

    processFiles (files) {
      this.validationError = null

      const currentAttachments = this.value || []

      // Check count limit
      if (currentAttachments.length + files.length > MAX_FILES) {
        this.validationError = `Možete priložiti najviše ${MAX_FILES} datoteka.`
        return
      }

      // Validate each file type
      for (const file of files) {
        const ext = '.' + file.name.split('.').pop().toLowerCase()
        const mimeOk = ALLOWED_MIME_TYPES.includes(file.type)
        const extOk = ALLOWED_EXTENSIONS.includes(ext)
        if (!mimeOk && !extOk) {
          this.validationError = `Datoteka "${file.name}" nije dopuštenog tipa. Dopušteni tipovi: PDF, PNG, JPG, CSV, XLSX, ODS.`
          return
        }
      }

      // Check total size
      const currentSize = currentAttachments.reduce((sum, a) => sum + (a.size || 0), 0)
      const newSize = files.reduce((sum, f) => sum + f.size, 0)
      if (currentSize + newSize > MAX_TOTAL_SIZE) {
        this.validationError = `Ukupna veličina datoteka ne smije prelaziti 5MB. Trenutno: ${this.formatSize(currentSize + newSize)}.`
        return
      }

      // Build new attachment objects and start upload
      const newAttachments = files.map(file => ({
        file,
        name: file.name,
        mimeType: file.type || this.getMimeTypeFromExtension(file.name),
        size: file.size,
        status: 'pending',
        progress: 0,
        gcsPath: null,
        error: null
      }))

      const updated = [...currentAttachments, ...newAttachments]
      this.$emit('input', updated)

      // Start uploading each new file
      newAttachments.forEach((att, i) => {
        const globalIndex = currentAttachments.length + i
        this.uploadFile(att, globalIndex, updated)
      })
    },

    uploadFile (att, index, attachmentsArray) {
      const sanitizedName = att.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const gcsPath = `attachments/${this.companyId}/${Date.now()}/${sanitizedName}`

      // Mark as uploading
      const updated = [...attachmentsArray]
      updated[index] = { ...updated[index], status: 'uploading', progress: 0 }
      this.$emit('input', updated)

      const uploadTask = storage.ref(gcsPath).put(att.file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          const current = [...(this.value || [])]
          if (current[index]) {
            current[index] = { ...current[index], progress }
            this.$emit('input', current)
          }
        },
        (error) => {
          const current = [...(this.value || [])]
          if (current[index]) {
            current[index] = { ...current[index], status: 'error', error: error.message || 'Greška pri uploadanju' }
            this.$emit('input', current)
          }
        },
        () => {
          const current = [...(this.value || [])]
          if (current[index]) {
            current[index] = { ...current[index], status: 'done', progress: 100, gcsPath }
            this.$emit('input', current)
          }
        }
      )
    },

    removeAttachment (index) {
      const updated = [...(this.value || [])]
      updated.splice(index, 1)
      this.$emit('input', updated)
      this.validationError = null
    },

    fileIcon (mimeType) {
      if (!mimeType) return 'mdi-file-outline'
      if (mimeType === 'application/pdf') return 'mdi-file-pdf-box'
      if (mimeType.startsWith('image/')) return 'mdi-file-image'
      if (mimeType === 'text/csv') return 'mdi-file-delimited'
      if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'mdi-microsoft-excel'
      if (mimeType.includes('opendocument')) return 'mdi-file-table'
      return 'mdi-file-outline'
    },

    fileIconColor (mimeType) {
      if (!mimeType) return 'grey'
      if (mimeType === 'application/pdf') return 'red darken-1'
      if (mimeType.startsWith('image/')) return 'blue'
      if (mimeType === 'text/csv') return 'orange'
      if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'green darken-1'
      if (mimeType.includes('opendocument')) return 'teal'
      return 'grey'
    },

    formatSize (bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    getMimeTypeFromExtension (filename) {
      const ext = filename.split('.').pop().toLowerCase()
      const map = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        csv: 'text/csv',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ods: 'application/vnd.oasis.opendocument.spreadsheet'
      }
      return map[ext] || 'application/octet-stream'
    }
  }
}
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: #fafafa;
}

.upload-zone:hover {
  border-color: #90caf9;
  background-color: #f5f5f5;
}

.upload-zone--active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.upload-zone--error {
  border-color: #ef5350;
}

.upload-zone__content {
  pointer-events: none;
}

.attachment-item {
  border-bottom: 1px solid #f5f5f5;
}

.attachment-item:last-child {
  border-bottom: none;
}
</style>
